import { useContext, useEffect, useMemo } from 'react';
import { matchPath, useLocation, useParams } from 'react-router-dom';

import { selectCourse } from '../../db/courses/selectors';
import routes, { CourseParams } from '../../pages/routes';
import { LayoutContext } from '../../shared/Layout/Context';
import { useAppSelector } from '../../store/hooks';
import { CourseRole } from '../../types';
import { CourseTabs } from './types';

export const CourseTabIds = {
  [CourseTabs.TIMELINE]: 'course-timeline-tab',
  [CourseTabs.INFO]: 'course-info-tab',
  [CourseTabs.SYLLABUS]: 'course-syllabus-tab',
};

export const CourseTabPanelIds = {
  [CourseTabs.TIMELINE]: 'course-timeline-tabpanel',
  [CourseTabs.INFO]: 'course-info-tabpanel',
  [CourseTabs.SYLLABUS]: 'course-syllabus-tabpanel',
};

export default function useCourseHeaderVM() {
  const params = useParams<CourseParams>();
  const { pathname } = useLocation();

  const course = useAppSelector((state) => selectCourse(state, params.courseShortId));

  const canPublishInfo = useMemo(() => {
    const myRole = course?.myRole;
    const isInfoPublished = course?.status.isInfoPublished;
    return myRole === CourseRole.ADMIN && !isInfoPublished;
  }, [course?.myRole, course?.status.isInfoPublished]);

  const activeTab = useMemo(() => {
    const routeMap = {
      [routes.courseTimeline.path]: CourseTabs.TIMELINE,
      [routes.courseInfo.path]: CourseTabs.INFO,
      [routes.courseSyllabus.path]: CourseTabs.SYLLABUS,
    };

    for (const [path, tab] of Object.entries(routeMap)) {
      if (matchPath(path, pathname)) return tab;
    }

    return CourseTabs.TIMELINE;
  }, [pathname]);

  /** Tabs */

  const { setSelectedTabIds } = useContext(LayoutContext);

  useEffect(
    function onActiveTabChange() {
      setSelectedTabIds({
        tabId: CourseTabIds[activeTab],
        panelId: CourseTabPanelIds[activeTab],
      });
    },
    [setSelectedTabIds, activeTab]
  );

  useEffect(
    function onMount() {
      return function cleanup() {
        setSelectedTabIds(null);
      };
    },
    [setSelectedTabIds]
  );

  return { activeTab, canPublishInfo, params };
}
